import React, { useState } from "react";

import { Landmark } from "lucide-react";

import { cn } from "~/lib/utils";

// Local assets in public/banks/, named after fintoc institution ids.
const BANK_LOGO_SLUGS = new Set([
  "cl_banco_bci",
  "cl_banco_bice",
  "cl_banco_de_chile",
  "cl_banco_estado",
  "cl_banco_itau",
  "cl_banco_santander",
  "cl_banco_scotiabank",
]);

// Ordered so more specific names match before "chile".
const NAME_PATTERNS: Array<[RegExp, string]> = [
  [/bci/, "cl_banco_bci"],
  [/bice/, "cl_banco_bice"],
  [/estado/, "cl_banco_estado"],
  [/itau/, "cl_banco_itau"],
  [/santander/, "cl_banco_santander"],
  [/scotiabank/, "cl_banco_scotiabank"],
  [/chile/, "cl_banco_de_chile"],
];

const normalize = (value: string) =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export const getBankLogoSlug = (
  institutionId?: string | null,
  name?: string | null
): string | null => {
  if (institutionId && BANK_LOGO_SLUGS.has(institutionId)) {
    return institutionId;
  }
  if (name) {
    const normalized = normalize(name);
    const match = NAME_PATTERNS.find(([pattern]) => pattern.test(normalized));
    if (match) return match[1];
  }
  return null;
};

interface BankLogoProps {
  institutionId?: string | null;
  name?: string | null;
  className?: string;
}

export const BankLogo: React.FC<BankLogoProps> = ({
  institutionId,
  name,
  className,
}) => {
  const [failed, setFailed] = useState(false);
  const slug = getBankLogoSlug(institutionId, name);

  if (!slug || failed) {
    return (
      <span
        className={cn(
          "flex h-5 w-5 shrink-0 items-center justify-center rounded-sm bg-muted",
          className
        )}
      >
        <Landmark className="h-3 w-3 text-muted-foreground" />
      </span>
    );
  }

  return (
    <img
      src={`/banks/${slug}.png`}
      alt={name || "Banco"}
      className={cn("h-5 w-5 shrink-0 rounded-sm object-contain", className)}
      onError={() => setFailed(true)}
    />
  );
};
